import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Spinner,
  Text,
  Heading,
  Flex,
  Select,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  useDisclosure,
  Tooltip as ChakraTooltip,
  useToast,
} from "@chakra-ui/react";
import QRCode from "react-qr-code";
import { db, storage } from "../firebase";
import { useLanguage } from "../contexts/LanguageContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import BusinessCard from "../components/BusinessCard";
import Navbar from "../components/Navbar";
import AddToAppleWalletButton from "../components/AddToAppleWalletButton";
import {
  FaRegClock,
  FaHeart,
  FaRegHeart,
  FaSearch,
  FaTimes,
  FaQrcode,
  FaCopy,
  FaCheckCircle,
  FaRegCheckCircle,
} from "react-icons/fa";

const CustomerDashboard = () => {
  const { user } = useParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [campaignSupportedLanguages, setCampaignSupportedLanguages] = useState([
    "en",
    "fr",
  ]);
  const [campaignId, setCampaignId] = useState(null);
  const { language, translate } = useLanguage();
  const [redemptions, setRedemptions] = useState({});
  const [customerName, setCustomerName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [customerBusinesses, setCustomerBusinesses] = useState([]);
  const [showOnlyFavourites, setShowOnlyFavourites] = useState(false);
  const [hideRedeemed, setHideRedeemed] = useState(true);
  const [isValidCustomer, setIsValidCustomer] = useState(false);
  const {
    isOpen: isQrOpen,
    onOpen: onQrOpen,
    onClose: onQrClose,
  } = useDisclosure();
  const toast = useToast();
  const pageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  useEffect(() => {
    const fetchCustomerAndData = async () => {
      try {
        if (!user) {
          setError("User parameter is required.");
          return;
        }

        const customerRef = doc(db, "customers", user);
        const customerSnap = await getDoc(customerRef);

        if (!customerSnap.exists()) {
          setError("Customer not found.");
          return;
        }

        setIsValidCustomer(true);

        const customerData = customerSnap.data();
        setRedemptions(customerData.redemptions || {});
        setCustomerName(customerData.name || "");
        const customerBusinesses = customerData.businessIds || [];

        setCustomerBusinesses(customerBusinesses);

        const customerBusinessIds = customerBusinesses.map((business) =>
          typeof business === "string" ? business : business.id
        );

        const customerCampaignId = customerData.campaignId;
        if (!customerCampaignId) {
          setError("No campaign associated with this customer.");
          return;
        }

        setCampaignId(customerCampaignId);

        const campaignRef = doc(db, "campaigns", customerCampaignId);
        const campaignSnap = await getDoc(campaignRef);
        if (campaignSnap.exists()) {
          const campaignData = campaignSnap.data();
          setCampaign(campaignData);
          setCampaignSupportedLanguages(
            campaignData.supportedLanguages || ["en", "fr"]
          );
        } else {
          setError("Campaign not found.");
          return;
        }

        if (customerBusinessIds.length === 0) {
          setBusinesses([]);
          return;
        }

        const businessPromises = customerBusinessIds.map(async (businessId) => {
          const businessDoc = await getDoc(doc(db, "businesses", businessId));

          if (!businessDoc.exists() || !businessDoc.data().approved) {
            return null;
          }

          const businessData = businessDoc.data();
          let logoUrl = businessData.logoUrl || null;

          // Fall back to a direct Storage fetch only when the doc has no
          // logoUrl baked in (older records). Newer uploads write the public
          // URL straight onto the business doc.
          if (!logoUrl) {
            try {
              const logoRef = ref(storage, `business-logos/${businessId}`);
              logoUrl = await getDownloadURL(logoRef);
            } catch (err) {
              // Missing logo is expected for some businesses — fall through
              // and let the card's category-image fallback take over.
            }
          }

          return {
            id: businessId,
            ...businessData,
            logoUrl,
          };
        });

        const businessList = (await Promise.all(businessPromises))
          .filter((business) => business !== null)
          .map((business) => {
            const customerBusiness = customerBusinesses.find(
              (cb) => (typeof cb === "string" ? cb : cb.id) === business.id
            );
            return {
              ...business,
              favourite:
                typeof customerBusiness === "string"
                  ? false
                  : customerBusiness?.favourite || false,
            };
          });

        setBusinesses(businessList);
      } catch (err) {
        console.error("Error fetching data: ", err);
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerAndData();
  }, [user]);

  const getLocalizedText = (field) => {
    if (typeof field === "object" && field !== null) {
      return field[language] || field.en || "";
    }
    return field || "";
  };

  // Use the raw English value as a stable category key so a language switch
  // doesn't invalidate the user's selection; the label shown in the Select
  // is still localized.
  const getCategoryKey = (cat) => {
    if (typeof cat === "object" && cat !== null) return cat.en || "";
    return cat || "";
  };

  const categories = useMemo(() => {
    const localize = (field) => {
      if (typeof field === "object" && field !== null) {
        return field[language] || field.en || "";
      }
      return field || "";
    };
    const map = new Map();
    businesses.forEach((b) => {
      const key = getCategoryKey(b.category);
      if (!key) return;
      if (!map.has(key)) map.set(key, localize(b.category));
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, label]) => ({ key, label }));
  }, [businesses, language]);

  const filteredBusinesses = useMemo(() => {
    const localize = (field) => {
      if (typeof field === "object" && field !== null) {
        return field[language] || field.en || "";
      }
      return field || "";
    };
    const term = searchTerm.trim().toLowerCase();
    return businesses.filter((b) => {
      if (showOnlyFavourites && !b.favourite) return false;
      if (hideRedeemed && redemptions[b.id]) return false;
      if (selectedCategory !== "all") {
        if (getCategoryKey(b.category) !== selectedCategory) return false;
      }
      if (term) {
        const haystack = [
          localize(b.name),
          localize(b.category),
          localize(b.offerTitle),
          localize(b.offerDescription),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [
    businesses,
    searchTerm,
    selectedCategory,
    showOnlyFavourites,
    hideRedeemed,
    redemptions,
    language,
  ]);

  const toggleFavourite = async (businessId) => {
    if (!isValidCustomer) {
      console.error("Invalid customer access");
      return;
    }

    const now = Date.now();
    const lastToggle = localStorage.getItem(`lastToggle_${user}`);
    if (lastToggle && now - parseInt(lastToggle) < 1000) {
      return;
    }
    localStorage.setItem(`lastToggle_${user}`, now.toString());

    try {
      const customerRef = doc(db, "customers", user);
      const customerData = (await getDoc(customerRef)).data();

      const updatedBusinessIds = customerData.businessIds.map((business) => {
        if (business.id === businessId) {
          return { ...business, favourite: !business.favourite };
        }
        return business;
      });

      await updateDoc(customerRef, {
        businessIds: updatedBusinessIds,
      });

      setBusinesses((prevBusinesses) =>
        prevBusinesses.map((business) => {
          if (business.id === businessId) {
            return { ...business, favourite: !business.favourite };
          }
          return business;
        })
      );

      setCustomerBusinesses((prevBusinesses) =>
        prevBusinesses.map((business) => {
          if (business.id === businessId) {
            return { ...business, favourite: !business.favourite };
          }
          return business;
        })
      );
    } catch (error) {
      console.error("Error toggling favourite:", error);
    }
  };

  if (loading) {
    return (
      <Box
        minH="100vh"
        bg="primaryBackground"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        minH="100vh"
        bg="primaryBackground"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  const heroImage = campaign?.heroImage;
  const hasHero = !!heroImage?.url;
  const headingColor = hasHero ? "white" : undefined;
  const bodyColor = hasHero ? "whiteAlpha.900" : "gray.600";

  // Personalized title: "Andrew Barbour's Coupons" / "Coupons d'Andrew Barbour".
  // Falls back to the generic "Available Coupons" when the customer record
  // has no name. The French template uses "Coupons de {name}"; we elide
  // "de" → "d'" before vowels and silent h to read naturally (e.g.
  // "Coupons d'Andrew Barbour" rather than "Coupons de Andrew Barbour").
  const headingText = (() => {
    const trimmedName = (customerName || "").trim();
    if (!trimmedName) return translate("businessList.heading");
    const template = translate("businessList.headingPersonalized");
    let result = template.replace("{name}", trimmedName);
    if (language === "fr") {
      const firstChar = trimmedName.charAt(0).toLowerCase();
      if ("aeiouhàâäéèêëîïôöùûü".includes(firstChar)) {
        result = result.replace(`de ${trimmedName}`, `d'${trimmedName}`);
      }
    }
    return result;
  })();

  return (
    <Box
      minH="100vh"
      display="flex"
      flexDirection="column"
      bg="primaryBackground"
    >
      <Navbar external={true} supportedLanguages={campaignSupportedLanguages} />

      <Container
        maxW="1200px"
        px={{ base: 4, md: 6, lg: 8 }}
        py={{ base: 8, md: 12 }}
      >
        {/* Header / Hero Section */}
        <Box
          position="relative"
          width="100%"
          mb={hasHero ? 12 : 0}
          textAlign={{ base: "center", md: "left" }}
          borderRadius={hasHero ? "xl" : undefined}
          overflow={hasHero ? "hidden" : undefined}
          backgroundImage={hasHero ? `url(${heroImage.url})` : undefined}
          backgroundSize="cover"
          backgroundPosition="center"
          px={hasHero ? { base: 6, md: 10 } : 0}
          py={hasHero ? { base: 10, md: 16 } : 0}
          minH={hasHero ? { base: "320px", md: "380px" } : undefined}
        >
          {hasHero && (
            <Box
              position="absolute"
              inset={0}
              bgGradient="linear(to right, rgba(0,0,0,0.65), rgba(0,0,0,0.25))"
              pointerEvents="none"
            />
          )}
          <Box position="relative">
            <Heading as="h1" size="xl" mb={2} color={headingColor}>
              {headingText}
            </Heading>

            <Text fontSize="lg" color={bodyColor} mb={4}>
              {getLocalizedText(campaign?.name)}
            </Text>

            {campaign?.description &&
              getLocalizedText(campaign.description) && (
                <Text fontSize="md" color={bodyColor} mb={6} maxW="700px">
                  {getLocalizedText(campaign.description)}
                </Text>
              )}

            {campaign?.redemptionStartDate && campaign?.redemptionEndDate && (
              <Flex
                align="center"
                gap={2}
                color={bodyColor}
                justify={{ base: "center", md: "start" }}
              >
                <FaRegClock />
                <Text fontSize="sm" color={bodyColor}>
                  {translate("businessList.validFrom")}{" "}
                  <Text as="span" fontWeight="semibold">
                    {new Date(
                      campaign.redemptionStartDate?.toDate?.() ||
                        campaign.redemptionStartDate
                    ).toLocaleDateString(
                      language === "fr" ? "fr-CA" : "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </Text>{" "}
                  {translate("businessList.to")}{" "}
                  <Text as="span" fontWeight="semibold">
                    {new Date(
                      campaign.redemptionEndDate?.toDate?.() ||
                        campaign.redemptionEndDate
                    ).toLocaleDateString(
                      language === "fr" ? "fr-CA" : "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </Text>
                </Text>
              </Flex>
            )}
          </Box>

          {hasHero &&
            heroImage.source === "pexels" &&
            heroImage.attribution?.photographer && (
              <Text
                position="absolute"
                bottom={2}
                right={3}
                fontSize="xs"
                color="whiteAlpha.800"
                zIndex={1}
              >
                Photo by{" "}
                <Link
                  href={heroImage.attribution.photographerUrl}
                  isExternal
                  color="whiteAlpha.900"
                  textDecoration="underline"
                >
                  {heroImage.attribution.photographer}
                </Link>{" "}
                on{" "}
                <Link
                  href={heroImage.attribution.pexelsUrl}
                  isExternal
                  color="whiteAlpha.900"
                  textDecoration="underline"
                >
                  Pexels
                </Link>
              </Text>
            )}
        </Box>

        {/* Action row — Apple Wallet + QR code, sits below the hero image */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          align={{ base: "stretch", sm: "center" }}
          justify={{ base: "center", md: "flex-start" }}
          gap={3}
          mt={hasHero ? 0 : 6}
          mb={6}
        >
          {isValidCustomer && campaignId && (
            <AddToAppleWalletButton
              campaignId={campaignId}
              userId={user}
              size="sm"
            />
          )}
          <Button
            leftIcon={<FaQrcode />}
            onClick={onQrOpen}
            variant="outline"
            size="sm"
            bg="white"
          >
            {translate("businessList.openOnPhone")}
          </Button>
        </Flex>

        <Box width="100%" mb={12}>
          <Divider borderColor="gray.300" mb={8} />

          <Heading
            as="h2"
            size="md"
            mb={6}
            textAlign={{ base: "center", md: "left" }}
          >
            {translate("businessList.availableSavings")}
          </Heading>

          {/* Filter / Search */}
          <Flex
            direction={{ base: "column", sm: "row" }}
            gap={3}
            mb={6}
            width="100%"
            align={{ base: "stretch", sm: "center" }}
          >
            <InputGroup flex={1}>
              <InputLeftElement pointerEvents="none" color="gray.400">
                <FaSearch />
              </InputLeftElement>
              <Input
                placeholder={translate("businessList.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
                focusBorderColor="black"
              />
              {searchTerm && (
                <InputRightElement>
                  <IconButton
                    icon={<FaTimes />}
                    size="xs"
                    variant="ghost"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm("")}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            {categories.length > 1 && (
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                maxW={{ base: "100%", sm: "240px" }}
                bg="white"
                focusBorderColor="black"
              >
                <option value="all">
                  {translate("businessList.allCategories")}
                </option>
                {categories.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
            <Flex
              gap={2}
              alignSelf={{ base: "center", sm: "auto" }}
            >
              {(() => {
                const favLabel = translate(
                  showOnlyFavourites
                    ? "businessList.showAllCoupons"
                    : "businessList.showFavouritesOnly"
                );
                return (
                  <ChakraTooltip label={favLabel} hasArrow placement="top">
                    <IconButton
                      icon={showOnlyFavourites ? <FaHeart /> : <FaRegHeart />}
                      onClick={() => setShowOnlyFavourites((v) => !v)}
                      aria-label={favLabel}
                      variant="outline"
                      bg="white"
                      color={showOnlyFavourites ? "red.500" : "gray.600"}
                      borderColor="gray.200"
                    />
                  </ChakraTooltip>
                );
              })()}
              {(() => {
                const redeemedLabel = translate(
                  hideRedeemed
                    ? "businessList.showRedeemedCoupons"
                    : "businessList.hideRedeemedCoupons"
                );
                return (
                  <ChakraTooltip
                    label={redeemedLabel}
                    hasArrow
                    placement="top"
                  >
                    <IconButton
                      icon={
                        hideRedeemed ? <FaCheckCircle /> : <FaRegCheckCircle />
                      }
                      onClick={() => setHideRedeemed((v) => !v)}
                      aria-label={redeemedLabel}
                      variant="outline"
                      bg="white"
                      color={hideRedeemed ? "green.500" : "gray.600"}
                      borderColor="gray.200"
                    />
                  </ChakraTooltip>
                );
              })()}
            </Flex>
          </Flex>

          {filteredBusinesses.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text color="gray.500">
                {translate("businessList.noResults")}
              </Text>
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={{
                base: "minmax(0, 360px)",
                sm: "repeat(2, minmax(0, 360px))",
                xl: "repeat(3, minmax(0, 360px))",
              }}
              justifyContent={{ base: "center", sm: "start" }}
              gap={{ base: 6, md: 8, lg: 10 }}
              width="100%"
            >
              {filteredBusinesses.map((business) => (
                <BusinessCard
                  key={business.id}
                  redeemed={!!redemptions[business.id]}
                  showFavourite={true}
                  isFavourite={
                    customerBusinesses.find((b) => b.id === business.id)
                      ?.favourite || false
                  }
                  onToggleFavourite={() => toggleFavourite(business.id)}
                  business={{
                    id: business.id,
                    name: getLocalizedText(business.name),
                    category: getLocalizedText(business.category),
                    categoryKey: business.category,
                    description: getLocalizedText(business.description),
                    imageUrl: business.logoUrl,
                    websiteUrl: business.websiteUrl,
                    googleProfileUrl: business.googleProfileUrl,
                    coupon: {
                      title: getLocalizedText(business.offerTitle),
                      description: getLocalizedText(business.offerDescription),
                      termsAndExclusions: getLocalizedText(
                        business.termsAndExclusions
                      ),
                      maxValue: business.maxValue,
                    },
                  }}
                  translations={{
                    language,
                    couponDetails: translate("businessList.couponDetails"),
                    visitWebsite: translate("businessList.website"),
                    googleProfile: translate("businessList.googleProfile"),
                    termsAndExclusions: translate(
                      "businessList.termsAndExclusions"
                    ),
                    maxValue: translate("businessList.maxValue"),
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Modal isOpen={isQrOpen} onClose={onQrClose} isCentered size="sm">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {translate("businessList.scanToOpenOnPhone")}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <Box
                  bg="white"
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <QRCode value={pageUrl} size={224} level="H" />
                </Box>
                <Text fontSize="sm" color="gray.600" textAlign="center" wordBreak="break-all">
                  {pageUrl}
                </Text>
                <ChakraTooltip label="Copy link" hasArrow placement="top">
                  <IconButton
                    icon={<FaCopy />}
                    aria-label="Copy link"
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(pageUrl);
                        toast({
                          title: "Link copied to clipboard",
                          status: "success",
                          duration: 2000,
                          isClosable: true,
                        });
                      } catch (err) {
                        toast({
                          title: "Couldn't copy link",
                          status: "error",
                          duration: 2000,
                          isClosable: true,
                        });
                      }
                    }}
                  />
                </ChakraTooltip>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default CustomerDashboard;
